import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { DeskState } from './desk-model';
import { StayNightsControl } from './stay-nights-control';

// The desk's one-line goal: a segmented bar (confirmed → on hold → resolved
// without → still to place) + the number the operator works to zero. All-green
// when the shift is done. Token-native; same semantic hues as the rest.
//
// It also carries HOW LONG the stay is (feature `stay-nights`, N8). The shift's
// goal is not just "everyone placed" but "everyone placed for as long as they are
// actually stranded" — a three-night disruption booked one night at a time is the
// bug this feature exists to fix, and the number belongs in the sentence that
// states the goal. See `stay-nights-control.tsx` on why it is not in the action
// bar. Skipped on an EMPTY walk only: with no rows there is nothing to apply a
// blanket number to, and an unassessed roster can still be pre-set.

const SEGMENTS: {
  key: 'CONFIRMED' | 'AWAITING_REPLY' | 'RESOLVED_WITHOUT' | 'REMAINDER';
  barClass: string;
  dotClass: string;
}[] = [
  { key: 'CONFIRMED', barClass: 'bg-emerald-500', dotClass: 'bg-emerald-500' },
  { key: 'AWAITING_REPLY', barClass: 'bg-sky-500', dotClass: 'bg-sky-500' },
  { key: 'RESOLVED_WITHOUT', barClass: 'bg-slate-400 dark:bg-slate-500', dotClass: 'bg-slate-400 dark:bg-slate-500' },
  { key: 'REMAINDER', barClass: 'bg-amber-500', dotClass: 'bg-amber-500' },
];

export function GoalBar({ desk, truncated }: { desk: DeskState; truncated: boolean }) {
  const { t } = useTranslation('tower');
  const remainder = desk.counts.NEEDS_HOTEL.pnrs + desk.counts.DECLINED.pnrs;
  // An EMPTY walk is not a victory — a cold roster / upstream blip must read as
  // "nothing loaded", never as "everyone accommodated".
  if (desk.rows.length === 0) {
    return (
      <section
        className="rounded-lg border border-border bg-card px-4 py-3"
        data-testid="hotel-goal-bar"
      >
        <p className="text-sm text-muted-foreground">{t('desk.goal.empty')}</p>
      </section>
    );
  }
  // Nothing in the queue lanes but rows still UNASSESSED (pre-rebooking): the
  // lodging need is UNKNOWN, not met — a neutral hold, never the success banner.
  if (
    desk.counts.UNASSESSED.pnrs > 0 &&
    desk.counts.NEEDS_HOTEL.pnrs === 0 &&
    desk.counts.DECLINED.pnrs === 0 &&
    desk.counts.AWAITING_REPLY.pnrs === 0
  ) {
    return (
      <section
        className="rounded-lg border border-border bg-card px-4 py-3"
        data-testid="hotel-goal-bar"
      >
        <p className="text-sm font-medium" data-testid="goal-unassessed">
          {t('desk.goal.unassessed')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('desk.goal.unassessedCount', {
            pnrs: t('count.pnrs', { count: desk.counts.UNASSESSED.pnrs }),
            pax: desk.counts.UNASSESSED.pax,
          })}
          {truncated ? t('desk.goal.partialRoster') : ''}
        </p>
        <StayNightsControl desk={desk} />
      </section>
    );
  }
  const remainderPax = desk.counts.NEEDS_HOTEL.pax + desk.counts.DECLINED.pax;
  const values: Record<(typeof SEGMENTS)[number]['key'], number> = {
    CONFIRMED: desk.counts.CONFIRMED.pnrs,
    AWAITING_REPLY: desk.counts.AWAITING_REPLY.pnrs,
    RESOLVED_WITHOUT: desk.counts.RESOLVED_WITHOUT.pnrs,
    REMAINDER: remainder,
  };
  const total = Math.max(1, desk.goalPnrs);

  return (
    <section
      className={cn(
        'rounded-lg border px-4 py-3',
        desk.allSettled
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
          : 'border-border bg-card',
      )}
      data-testid="hotel-goal-bar"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {desk.allSettled ? (
          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
            {desk.goalPnrs === 0
              ? t('desk.goal.noOvernight')
              : t('desk.goal.allAccommodated')}
          </p>
        ) : (
          <p className="text-sm">
            <span className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400" data-testid="goal-remainder">
              {remainder}
            </span>{' '}
            <span className="font-semibold">{t('desk.goal.toPlace', { count: remainder })}</span>{' '}
            <span className="text-muted-foreground">
              {t('desk.goal.remainderDetail', {
                pax: remainderPax,
                awaiting: desk.counts.AWAITING_REPLY.pnrs,
              })}
            </span>
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t('desk.goal.needOvernight', { pnrs: desk.goalPnrs, pax: desk.goalPax })}
          {truncated ? t('desk.goal.partialRoster') : ''}
        </p>
      </div>
      <div className="mt-3 flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted">
        {SEGMENTS.map((seg) =>
          values[seg.key] > 0 ? (
            <div
              key={seg.key}
              className={cn('h-full', seg.barClass)}
              style={{ width: `${(values[seg.key] / total) * 100}%` }}
              title={t('desk.goal.segmentTitle', {
                label: t(`desk.goal.segment.${seg.key}`),
                count: values[seg.key],
              })}
            />
          ) : null,
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        {SEGMENTS.map((seg) => (
          <span key={seg.key} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span aria-hidden className={cn('h-2 w-2 rounded-full', seg.dotClass)} />
            {values[seg.key]} {t(`desk.goal.segment.${seg.key}`)}
          </span>
        ))}
      </div>
      <StayNightsControl desk={desk} />
    </section>
  );
}
